import { Patient } from 'fhir/r4'
import { useState, useEffect, useRef } from 'react'
import { startSxpProxy } from '../../../utils/api'
import {
  CHAT_PROJECT_ID,
  MEMBERSHIP_PROJECT_ID,
} from '../../../utils/constants'
import { makeName } from '../../lms/utils'
import '../membership.scss'
import { useAppSelector } from '../../../app/hooks'
import { selectSelectedLocationId } from '../../location/locationSlice'
import { MemberPatient, SearchPatient } from '../models'
import SelectableSearch from '../../../components/SelectableSearch'
import { useParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import ToastMessage from '../../lms/components/ToastMessage'
import { toastOptions } from '../../lms/models'
import EditMemberTable from './EditMemberTable'
import SearchPatients from './SearchPatients'

const EditPatientSearch = () => {
  const location = useAppSelector(selectSelectedLocationId)

  const patientsTableContainerRef = useRef<HTMLDivElement | null>(null)
  const [patients, setPatients] = useState<SearchPatient[]>([])
  const [previousMembers, setPreviousMembers] = useState<MemberPatient[]>([])
  const [members, setMembers] = useState<MemberPatient[]>([])
  const { id } = useParams()

  const navigate = useNavigate()
  const [show, setShow] = useState<boolean>(false)
  const onboardMap: Record<string, boolean> = {}

  const handleSearch = (query: string, param: string) => {
    if (!query) {
      setPatients([])
      setShow(false)
      return
    }
    setShow(true)
    const intent = 'getPatientsAPI'
    let q = ''
    switch (query) {
      case 'ID':
        q = 'identifier'
        break
      case 'Phone':
        q = 'phone'
        break
      case 'Name':
        q = 'name'
        break
    }
    const state = {
      pageSize: 20,
      query: q,
      value: param ? param : undefined,
    }
    startSxpProxy(CHAT_PROJECT_ID, intent, state)
      ?.then((data) => {
        const entries = data?.data?.data?.entry || data?.data?.entry
        if (entries?.length) {
          setPatients(formatPatients(entries))
        } else {
          setPatients([])
        }
      })
      .catch((err) => {
        console.log(err)
      })
  }

  const fetchMembershipLineDetails = async () => {
    try {
      const resp = await startSxpProxy(
        MEMBERSHIP_PROJECT_ID,
        'membershipLine',
        {
          location,
          lineId: id,
        }
      )

      const membership = resp?.data?.[0]
      if (!membership) return
      const relationMap: Record<string, string> = {}

      membership?.membership_partner_ids?.forEach(
        (item: {
          partner_id?: { ref?: string }
          relation_with_primary_partner?: string
        }) => {
          const ref = item?.partner_id?.ref
          if (ref) {
            relationMap[ref] = item?.relation_with_primary_partner ?? 'self'
          }
        }
      )
      const memberUhidsArray = Object.keys(relationMap)
      if (memberUhidsArray.length === 0) return
      const membersOne = memberUhidsArray.join(',')
      const patientResp = await startSxpProxy(
        MEMBERSHIP_PROJECT_ID,
        'getPatientById',
        {
          patientIdentifier: membersOne,
        }
      )

      const memberDetails =
        patientResp?.data?.data?.entry?.map(
          (e: { resource: any }) => e.resource
        ) ?? []

      const uhidObj = memberDetails.reduce(
        (acc: any, cur: { identifier: { value: any }[] }) => {
          const uhid = cur?.identifier?.[2]?.value
          if (uhid) acc[uhid] = cur
          return acc
        },
        {}
      )
      const memberDetailArray: MemberPatient[] = memberUhidsArray.map(
        (uhid, index) => {
          const role = uhidObj[uhid]

          membership?.membership_partner_ids?.forEach(
            (item: {
              partner_id?: { ref?: string }
              is_onboarded?: boolean
            }) => {
              const ref = item?.partner_id?.ref
              if (ref) {
                onboardMap[ref] = Boolean(item?.is_onboarded)
              }
            }
          )
          return {
            id: role?.id ?? '',
            uhid,
            name: makeName(role?.name),
            registration: role?.identifier?.[3]?.value,
            gender:
              role?.gender === 'male'
                ? 'Male'
                : role?.gender === 'female'
                ? 'Female'
                : 'Others',
            dob: role?.birthDate ?? '',
            thopId: role?.identifier?.[0]?.value ?? '',
            isSelected: true,
            isNominee: index === 0,
            relation: relationMap[uhid] ?? 'self',
            is_onboarded: onboardMap[uhid] ?? false,
          }
        }
      )

      setMembers(memberDetailArray)
      setPreviousMembers(memberDetailArray)
    } catch (error) {
      console.error('Failed to fetch membership line details', error)
    }
  }

  useEffect(() => {
    if (id && location) {
      fetchMembershipLineDetails()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, location])

  const formatPatients = (entry: { resource: Patient }[]): SearchPatient[] => {
    return (
      entry.map((en) => {
        const gender = en.resource.gender
        const pat: SearchPatient = {
          id: en.resource.id ?? '',
          name: makeName(en.resource.name),
          gender:
            gender === 'male'
              ? 'Male'
              : gender === 'female'
              ? 'Female'
              : 'Others',
          dob: en.resource.birthDate ?? '',
          uhid: en.resource?.identifier?.[2]?.value ?? '',
          thopId: en.resource?.identifier?.[0]?.value ?? '',
          registration: en.resource?.identifier?.[3]?.value ?? 'ad',
          isSelected: false,
          isNominee: false,
        }
        return pat
      }) ?? []
    )
  }
  const handleMemberSelect = (id: string, relation: string) => {
    const selectedMember = patients.find((member) => member.id === id)
    if (selectedMember) {
      startSxpProxy(MEMBERSHIP_PROJECT_ID, 'membershipLines', {
        location,
        partner: selectedMember?.uhid,
      })
        ?.then(async () => {
          setMembers((prev) => [
            ...prev,
            {
              ...selectedMember,
              relation,
            },
          ])
        })
        .catch((err) => {
          console.log(err)
          toast(
            <ToastMessage message={err?.response?.data?.error ?? 'errorMsg'} />,
            {
              ...toastOptions,
              type: 'error',
            }
          )
        })
    }
  }
  useEffect(() => {
    const resizeHandler = () => {
      const container = patientsTableContainerRef.current
      if (container) {
        const availableHeight: any =
          window.innerHeight - container.getBoundingClientRect().top
        container.style.maxHeight = `${availableHeight - 25}px`
      }
    }

    window.addEventListener('resize', resizeHandler)
    resizeHandler()

    return () => {
      window.removeEventListener('resize', resizeHandler)
    }
  }, [])
  const handleMemberUnSelect = (id: string) => {
    const member = members.filter((d) => d.id !== id)
    setMembers(member)
  }
  const handleMembership = () => {
    navigate('/membership')
  }

  return (
    <>
      <div className='layoutcontent'>
        <div className='patients-filters'>
          <SelectableSearch
            items={['ID', 'Phone', 'Name']}
            onSearch={handleSearch}
          />
          <button className={'btn btn-primaryBtn'} onClick={handleMembership}>
            Back To Membership
          </button>
        </div>
        <EditMemberTable
          members={members}
          onMemberUnSelect={handleMemberUnSelect}
          previousMembers={previousMembers}
        />
        {show && (
          <SearchPatients
            patients={patients}
            onMemberSelect={handleMemberSelect}
            members={members}
            plantype={''}
          />
        )}
      </div>
    </>
  )
}
export default EditPatientSearch
